/** 侧栏 key → cryptoPage 请求 priceSlug */
export const CRYPTO_PRICE_SLUG_MAP = {
  m5: '5M',
  m15: '15M',
  m30: '30m',
  h1: '1H',
  h4: '4h',
  d1: 'daily',
  weekly: 'weekly',
  monthly: 'monthly',
  yearly: 'yearly',
  preMarket: 'pre-market',
  etf: 'etf',
};

/** priceSlug / currencySlug → 侧栏 key（反向索引） */
const PRICE_SLUG_TO_SIDEBAR_KEY = Object.fromEntries(
  Object.entries(CRYPTO_PRICE_SLUG_MAP).map(([key, slug]) => [String(slug).toLowerCase(), key])
);

/** 已知币种 slug（展示/accent 兜底；侧栏列表以 /currency/page 为准） */
export const CRYPTO_CURRENCY_SLUGS = new Set([
  'bitcoin',
  'ethereum',
  'solana',
  'xrp',
  'dogecoin',
  'bnb',
  'microstrategy',
]);

function normalizeKey(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

/** 侧栏选中项是否应按 currencySlug 请求 Feed */
export function isCryptoCurrencyFilter(filter) {
  const key = normalizeKey(filter);
  if (!key || key === 'all') return false;
  if (Object.prototype.hasOwnProperty.call(CRYPTO_PRICE_SLUG_MAP, key)) return false;
  if (PRICE_SLUG_TO_SIDEBAR_KEY[key]) return false;
  return true;
}

/** 从接口行解析展示用数量；无有效值返回 null */
export function resolveCryptoFilterCount(row) {
  if (!row || typeof row !== 'object') return null;
  const raw =
    row.count ??
    row.marketCount ??
    row.marketNum ??
    row.collectionCount ??
    row.total ??
    row.num ??
    null;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n);
}

function resolveSidebarKeyFromNormalized(normalized) {
  if (!normalized) return '';
  if (normalized === 'all') return 'all';
  if (normalized === 'premarket' || normalized === 'pre-market') return 'preMarket';
  if (Object.prototype.hasOwnProperty.call(CRYPTO_PRICE_SLUG_MAP, normalized)) return normalized;
  if (CRYPTO_CURRENCY_SLUGS.has(normalized)) return normalized;
  if (PRICE_SLUG_TO_SIDEBAR_KEY[normalized]) return PRICE_SLUG_TO_SIDEBAR_KEY[normalized];
  return '';
}

/** 动态币种 slug：非周期筛选项则原样返回 */
function resolveDynamicCurrencyKey(normalized) {
  if (!normalized || normalized === 'all') return '';
  if (Object.prototype.hasOwnProperty.call(CRYPTO_PRICE_SLUG_MAP, normalized)) return '';
  if (PRICE_SLUG_TO_SIDEBAR_KEY[normalized]) return '';
  return normalized;
}

/** 将接口行映射到侧栏 filter key（all / m5 / bitcoin …） */
export function resolveCryptoSidebarKey(row) {
  if (!row || typeof row !== 'object') return '';

  const direct = normalizeKey(row.filterKey ?? row.key ?? row.sidebarKey);
  const fromDirect = resolveSidebarKeyFromNormalized(direct);
  if (fromDirect) return fromDirect;
  const dynamicDirect = resolveDynamicCurrencyKey(direct);
  if (dynamicDirect) return dynamicDirect;

  const priceSlug = normalizeKey(row.priceSlug);
  if (priceSlug) {
    const fromPrice = resolveSidebarKeyFromNormalized(priceSlug);
    if (fromPrice) return fromPrice;
  }

  const currencySlug = normalizeKey(row.currencySlug ?? row.currency);
  if (currencySlug) {
    const fromCurrency = resolveSidebarKeyFromNormalized(currencySlug);
    if (fromCurrency) return fromCurrency;
    const dynamicCurrency = resolveDynamicCurrencyKey(currencySlug);
    if (dynamicCurrency) return dynamicCurrency;
  }

  const slug = normalizeKey(row.slug);
  const fromSlug = resolveSidebarKeyFromNormalized(slug);
  if (fromSlug) return fromSlug;
  return resolveDynamicCurrencyKey(slug);
}

/** 接口列表 → key → count */
export function buildCryptoFilterCountMap(apiRows) {
  const map = new Map();
  const list = Array.isArray(apiRows) ? apiRows : [];
  for (const row of list) {
    const key = resolveCryptoSidebarKey(row);
    const count = resolveCryptoFilterCount(row);
    if (!key || count == null) continue;
    map.set(key, count);
  }
  return map;
}

/** 静态侧栏模板 + 接口数量 */
export function mergeCryptoSidebarRows(templateRows, apiRows) {
  const countMap = buildCryptoFilterCountMap(apiRows);
  return templateRows.map((row) => {
    if (row.type === 'divider') return { ...row };
    const count = countMap.has(row.key) ? countMap.get(row.key) : null;
    return { ...row, count };
  });
}

export function normalizeCryptoListPayload(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

/** CurrencyVo → 侧栏币种行；禁用项返回 null */
export function mapCurrencyVoToSidebarRow(row) {
  if (!row || typeof row !== 'object') return null;
  const key = normalizeKey(row.slug);
  if (!key) return null;
  if (row.openStatus != null && Number(row.openStatus) !== 0) return null;
  const label = String(row.currencyName ?? row.symbol ?? key).trim() || key;
  const iconUrl = String(row.icon ?? '').trim();
  const weight = Number(row.weight);
  return {
    key,
    label,
    iconUrl: iconUrl || undefined,
    weight: Number.isFinite(weight) ? weight : 0,
  };
}

/** /currency/page rows → 侧栏币种区（按 weight 降序） */
export function buildCurrencySidebarRows(apiRows) {
  const list = Array.isArray(apiRows) ? apiRows : [];
  return list
    .map(mapCurrencyVoToSidebarRow)
    .filter(Boolean)
    .sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0))
    .map(({ weight: _weight, ...row }) => row);
}
